/* eslint-disable react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { capitalizeFirstLetter } from '../../lib/utils';

import { useApi } from '../../contexts/ApiContext';
import { useUser } from '../../contexts/AuthContext';
import { useUrlGenerator } from '../../contexts/RoutesContext';
import RoundedButton from '../buttons/Rounded';
import SubmitButton from '../buttons/Submit';
import FormGroup from '../fields/FormGroup';
import TextField from '../fields/Text';
import CloseIcon from '../icons/Close';

import styles from '../../../styles/forms/delete-profile.module.scss';

const propTypes = {
    onComplete: PropTypes.func,
};

const defaultProps = {
    onComplete: null,
};

const DeleteProfileForm = ({ onComplete }) => {
    const route = useUrlGenerator();
    const intl = useIntl();
    const api = useApi();
    const user = useUser();
    const { provider = null } = user || {};

    const update = useCallback((action, data) => api.account.delete(data), [api]);
    const {
        fields,
        onSubmit,
        loading,
        generalError = null,
    } = useForm({
        postForm: update,
        fields: ['email'],
        value: null,
        ...(onComplete !== null ? { onComplete } : { onComplete: () => {} }),
    });

    return (
        <form className={styles.container} action={route('account.update')} onSubmit={onSubmit}>
            {provider !== null ? (
                <p className={styles.provider}>
                    <FormattedMessage
                        defaultMessage="You are connected with {provider}"
                        description="Message"
                        values={{ provider: capitalizeFirstLetter(provider) }}
                    />
                </p>
            ) : null}
            <FormGroup
                {...fields.email}
                label={<FormattedMessage defaultMessage="Email" description="Field label" />}
                className={styles.formGroup}
                showLabel
            >
                <TextField
                    {...fields.email}
                    className={classNames(styles.input, styles.inputText)}
                    type="email"
                    required
                    placeholder={intl.formatMessage({
                        defaultMessage: 'Your email',
                        description: 'Field placeholder',
                    })}
                    autofill={false}
                    invalid={generalError !== null}
                />
                {generalError !== null ? (
                    <FormattedMessage
                        defaultMessage="Please enter the email you used to create your account."
                        description="Error message"
                    />
                ) : null}
            </FormGroup>

            <div className={styles.formActions}>
                <SubmitButton
                    className={styles.formSubmitBtn}
                    icon={<CloseIcon />}
                    loading={loading}
                >
                    <FormattedMessage
                        defaultMessage="Delete my profile"
                        description="Button label"
                    />
                </SubmitButton>
                <RoundedButton href={route('account')} secondary isDark>
                    <FormattedMessage defaultMessage="Back" description="Button label" />
                </RoundedButton>
            </div>
        </form>
    );
};
DeleteProfileForm.propTypes = propTypes;
DeleteProfileForm.defaultProps = defaultProps;

export default DeleteProfileForm;
